import type { RootState } from '@src/store/store';
import type { Draft, PayloadAction } from '@reduxjs/toolkit';
import type {
  IAccountDeletionFeedback,
  IAccountDeletionFeedbackListResponse,
} from 'src/types/feedback';

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import api, { endpoints } from 'src/lib/axios';

// ----------------------------------------------------------------------

interface FeedbackState {
  accountDeletion: {
    data: IAccountDeletionFeedback[];
    total: number;
    page: number;
    limit: number;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string;
  };
}

export const initialState: FeedbackState = {
  accountDeletion: {
    data: [],
    total: 0,
    page: 1,
    limit: 20,
    status: 'idle',
    error: '',
  },
};

// ----------------------------------------------------------------------
// Thunks
// ----------------------------------------------------------------------

interface FetchFeedbackParams {
  page?: number;
  limit?: number;
}

export const loadAccountDeletionFeedback = createAsyncThunk<
  IAccountDeletionFeedbackListResponse,
  FetchFeedbackParams | void
>('feedback/loadAccountDeletionFeedback', async (paramsInput) => {
  const params = paramsInput || {};
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.limit) queryParams.append('limit', params.limit.toString());

  const url = `${endpoints.admin.feedback.accountDeletion}${queryParams.toString() ? `?${queryParams}` : ''}`;
  const response = await api.get<IAccountDeletionFeedbackListResponse>(url);
  return response.data;
});

// ----------------------------------------------------------------------
// Slice
// ----------------------------------------------------------------------

const slice = createSlice({
  name: 'feedback',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadAccountDeletionFeedback.pending, (state: Draft<FeedbackState>) => {
        state.accountDeletion.status = 'loading';
      })
      .addCase(
        loadAccountDeletionFeedback.fulfilled,
        (
          state: Draft<FeedbackState>,
          action: PayloadAction<IAccountDeletionFeedbackListResponse>
        ) => {
          state.accountDeletion.status = 'succeeded';
          state.accountDeletion.data = action.payload.data;
          state.accountDeletion.total = action.payload.total;
          state.accountDeletion.page = action.payload.page;
          state.accountDeletion.limit = action.payload.limit;
          state.accountDeletion.error = '';
        }
      )
      .addCase(loadAccountDeletionFeedback.rejected, (state: Draft<FeedbackState>, action) => {
        state.accountDeletion.status = 'failed';
        state.accountDeletion.error =
          action.error.message || 'Failed to load account deletion feedback';
      });
  },
});

export const feedbackReducer = slice.reducer;

// ----------------------------------------------------------------------
// Selectors
// ----------------------------------------------------------------------

export const selectAccountDeletionFeedback = (state: RootState) => state.feedback.accountDeletion;
